<template>
  <CurrentEventDataProvider v-slot="{ title, data, features }">
    <div class="h-full w-full flex flex-col bg-white text-dark px-4">
      <!-- Navbar -->
      <div
        class="navbar pt-4 flex justify-between items-center font-bold bg-white sticky top-0 z-30"
      >
        <!-- Logo & Event name -->
        <div class="logo flex items-center">
          <img
            :src="data.logoUrl"
            :alt="data.logoUrl && $t('Views.Login.eventLogo')"
            class="object-scale-down h-10 md:h-16 w-auto"
          />
        </div>

        <!-- Support & Logout -->
        <div>
          <button
            v-show="chatWidgetLoaded"
            v-if="features.stageSupport"
            class="button button-primary button-xs text-xxs md:text-xs sm:inline-block"
            :aria-label="$t('Views.Login.support')"
            @click="toggleLiveChatWindow"
          >
            {{ $t('Views.Login.support') }}
          </button>
        </div>
      </div>

      <!-- Content -->
      <div
        class="flex flex-col flex-1 bg-light mt-4 md:mb-8 md:ml-8 md:mr-8 items-center justify-center"
        style="
          color: var(--login-color-text);
          background-color: var(--login-background-color);
        "
      >
        <div class="text-center mb-2 md:mb-8">
          <h1 class="font-bold text-md md:text-lg mb-4">
            {{ title }}
          </h1>
        </div>

        <Card v-if="data.active === '1'" class="p-8 md:p-10 md:w-2/5">
          <form
            method="post"
            autocapitalize="none"
            @submit.prevent="submitForm"
          >
            <div class="space-y-4">
              <!-- Password -->
              <div class="relative">
                <input
                  v-model="form.password"
                  :placeholder="$t('Views.ResetPassword.password')"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  :readonly="settingPassword"
                  class="p-4 border-none text-dark w-full rounded transition-all duration-200 ease-in-out pr-12"
                  :class="{
                    'bg-grey': settingPassword,
                    'bg-light': !settingPassword
                  }"
                  :aria-label="$t('Views.ResetPassword.password')"
                  @keydown.space.prevent
                />
                <i
                  class="fal fa-info-circle text-dark absolute top-1/2 -translate-y-1/2 cursor-pointer"
                  style="right: 1rem"
                  @mouseenter="showTooltip = true"
                  @mouseleave="showTooltip = false"
                />
                <i
                  :class="showPassword ? 'fal fa-eye-slash' : 'fal fa-eye'"
                  class="text-dark absolute top-1/2 -translate-y-1/2 cursor-pointer"
                  style="right: 3rem"
                  @click="showPassword = !showPassword"
                />
                <!-- Tooltip -->
                <div
                  v-if="showTooltip"
                  class="absolute right-0 top-full mt-2 bg-gray-800 text-white text-sm p-2 shadow-lg z-10"
                  style="max-width: 200px; text-align: center"
                >
                  {{ $t('Views.ResetPassword.passwordTooltip') }}
                </div>
              </div>

              <!-- Confirm Password -->
              <div class="relative">
                <input
                  v-model="form.confirmPassword"
                  :placeholder="$t('Views.ResetPassword.confirmPassword')"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  name="confirmPassword"
                  :readonly="settingPassword"
                  class="p-4 border-none text-dark w-full rounded transition-all duration-200 ease-in-out pr-12"
                  :class="{
                    'bg-grey': settingPassword,
                    'bg-light': !settingPassword
                  }"
                  :aria-label="$t('Views.ResetPassword.confirmPassword')"
                  @keydown.space.prevent
                />
                <i
                  class="fal fa-info-circle text-dark absolute top-1/2 -translate-y-1/2 cursor-pointer"
                  style="right: 1rem"
                  @mouseenter="showConfirmTooltip = true"
                  @mouseleave="showConfirmTooltip = false"
                />
                <i
                  :class="
                    showConfirmPassword ? 'fal fa-eye-slash' : 'fal fa-eye'
                  "
                  class="text-dark absolute top-0 my-5 cursor-pointer"
                  style="right: 3rem"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
                <!-- Tooltip -->
                <div
                  v-if="showConfirmTooltip"
                  class="absolute right-0 top-full mt-2 bg-gray-800 text-white text-sm p-2 w-64 shadow-lg z-10"
                  style="max-width: 200px; text-align: center"
                >
                  {{ $t('Views.ResetPassword.passwordTooltip') }}
                </div>
              </div>

              <div class="flex items-baseline justify-between flex-col">
                <div v-if="resetPasswordFailed" class="flex-1 mb-4">
                  <transition name="fade">
                    <span class="form-error text-sm font-bold">
                      <i class="fas fa-exclamation-circle mr-1" />
                      {{ $t(errorMessage) }}
                    </span>
                  </transition>
                </div>

                <div
                  class="w-full flex flex-col-reverse md:flex-row md:justify-end"
                >
                  <TappinButton
                    type="submit"
                    :disabled="settingPassword || loading"
                    class="whitespace-nowrap button button-primary mt-2 md:mt-0"
                    :aria-label="
                      settingPassword
                        ? $t('Views.PollResults.loading')
                        : $t('Views.Sponsor.ContactForm.submit')
                    "
                    :icon="settingPassword ? 'circle-notch' : ''"
                    :custom_caption="$t('Views.Sponsor.ContactForm.submit')"
                  />
                </div>
              </div>
            </div>
          </form>
        </Card>
        <Card v-else class="p-10 lg:w-2/5">
          <span class="text-center" :aria-label="$t('Views.Login.eventClosed')">
            <i class="fas fa-exclamation-circle mr-1" aria-hidden="true" />
            {{ $t('Views.Login.eventClosed') }}
          </span>
        </Card>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import TappinButton from '@/components/buttons/TappinButton';
import Card from '@/components/shared/Card';
import { togglesSupportChat } from '@/mixins';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ResetPassword',
  components: {
    Card,
    TappinButton
  },
  mixins: [togglesSupportChat],
  data() {
    return {
      loading: false,
      resetPasswordFailed: false,
      form: {
        password: '',
        confirmPassword: ''
      },
      showPassword: false,
      showConfirmPassword: false,
      showTooltip: false,
      showConfirmTooltip: false,
      tosText: null,
      twoFact: null,
      twoFactPinId: null,
      errorMessage: 'Views.ResetPassword.passwordResetFailed'
    };
  },
  computed: {
    ...mapGetters('CurrentEvent', [
      'features',
      'isLobbyEnabled',
      'isStageEnabled',
      'isWebappEnabled',
      'getId',
      'getTwoFact',
      'features'
    ]),
    ...mapGetters('Authentication', [
      'isSetPasswordError',
      'settingPassword',
      'isSetPasswordSuccess'
    ]),
    ...mapGetters('Terms', ['termsShown']),

    shouldRenderError() {
      return window.innerWidth > 768 && this.form.platform === null;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  methods: {
    ...mapActions('Authentication', ['doSetPassword']),
    ...mapActions('Terms', ['setTermsAsShown']),
    handleResize() {
      this.$forceUpdate();
    },
    isValidPassword(password) {
      return /^(?=.*[A-Z])(?=.*\d).{6,}$/.test(password);
    },
    async submitForm(e) {
      this.resetPasswordFailed = false;
      this.loading = true;
      this.errorMessage = 'Views.ResetPassword.passwordResetFailed';

      if (this.form.password != this.form.confirmPassword) {
        this.resetPasswordFailed = true;
        this.errorMessage =
          'Views.ResetPassword.passwordAndConfirmPasswordSame';
        this.loading = false;
        return;
      }

      if (!this.isValidPassword(this.form.password)) {
        this.resetPasswordFailed = true;
        this.errorMessage = 'Views.ResetPassword.invalidPassword';
        this.loading = false;
        return;
      }

      await this.doSetPassword({
        password: this.form.password,
        accessKey: this.$route.params.accessKey,
        eventId: this.getId
      });

      if (this.isSetPasswordError) {
        this.resetPasswordFailed = true;
        this.loading = false;
        return;
      }

      if (this.isSetPasswordSuccess) {
        localStorage.setItem(`tokens_${this.getId}`, null);
        this.$router.push({ name: 'login' });
        this.$notify({
          group: 'default',
          title: this.$t(
            'Components.RecoveryPasswordModal.resetEmailPasswordSuccessMessage'
          )
        });
      } else {
        this.$router.push({ name: 'resetFallback' });
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>
